import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SeaLevelMeasurement,
  SeaTemperatureMeasurement,
  useThirdPartyApi,
} from "~/api/GeneratedApi";
import { TabPanel } from "~/components/GenericTab";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { SeaMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

const useStyles = makeStyles(() => ({
  tab: {
    margin: 0,
  },
}));

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: SeaMapItem;
  hoverData?: SeaMapItem;
};

type HoverProps = {
  hoverData: SeaMapItem;
  clickData?: SeaMapItem;
};

type HistoryMeasurement = {
  seaLevel: SeaLevelMeasurement;
  seaTemperature?: SeaTemperatureMeasurement;
};

export const SeaPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [historyMeasurements, setHistoryMeasurements] =
    useState<HistoryMeasurement[]>();
  const { t } = useTranslation("weather");
  const dateFormatter = useDateFormatter();
  const { getSeaHistory } = useThirdPartyApi();

  const data = clickData ?? hoverData;

  const { data: historyData, isLoading: historyLoading } = useCustomQuery(
    { key: "sea-history", id: clickData?.id },
    () => getSeaHistory({ station: clickData?.id }),
    {
      enabled: !!clickData?.id,
    },
  );

  // Map history response to state
  useEffect(() => {
    if (!historyData || !clickData?.id) return;
    const stationHistory = historyData?.find((h) => h.id === clickData.id);
    if (!stationHistory || !stationHistory.seaLevelMeasurements) return;
    const history: HistoryMeasurement[] =
      stationHistory.seaLevelMeasurements.map((l) => {
        // Matching temperature meas
        const temp = stationHistory?.seaTemperatureMeasurements?.find(
          (tmeas) => tmeas.timestamp.valueOf() === l.timestamp.valueOf(),
        );
        return {
          seaLevel: l,
          seaTemperature: temp,
        };
      });
    setHistoryMeasurements(history);
  }, [historyData, clickData?.id]);

  if (!data) return null;

  const {
    stationName,
    latestLevelValue,
    region,
    seaLevelMeasurements,
    seaTemperatureMeasurements,
  } = data;

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setTab(newValue);
  };

  return (
    <GenericPopup
      map={map}
      avatarText={
        latestLevelValue
          ? `${latestLevelValue.value} ${latestLevelValue.unit}`
          : "-"
      }
      title={`${t("sea-level")} - ${stationName ?? ""}`}
      subheader={region ?? ""}
      clicked={!!clickData}
      content={
        <>
          <Tabs
            value={tab}
            onChange={handleChange}
            TabIndicatorProps={{ className: classes.tab }}
            variant="fullWidth"
            indicatorColor="secondary"
          >
            <Tab label={t("latest-measurements")} className={classes.tab} />
            <Tab
              label={t("measurement-history")}
              disabled={historyLoading}
              className={classes.tab}
            />
          </Tabs>
          <TabPanel index={0} value={tab}>
            {seaLevelMeasurements?.length ? (
              <>
                <Typography align="center" variant="body2" noWrap>
                  {`${t("sea-level")} | ${t("sea-temperature")}`}
                </Typography>
                <Divider />
                <List>
                  {seaLevelMeasurements?.map((m, index) => {
                    // Matching temperature meas
                    const temp = seaTemperatureMeasurements?.find(
                      (tmeas) =>
                        tmeas.timestamp.valueOf() === m.timestamp.valueOf(),
                    );
                    return (
                      <ListItem
                        dense
                        alignItems="center"
                        style={{ textAlign: "center" }}
                        key={m.timestamp.valueOf()}
                        divider={index < seaLevelMeasurements.length - 1}
                      >
                        <ListItemText
                          primary={`${m.value} ${m.unit}${
                            temp ? ` | ${temp.value} ${temp.unit}` : ""
                          }`}
                          secondary={dateFormatter({ date: m.timestamp })}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : (
              <Typography align="center" variant="body2">
                {t("no-measurements")}
              </Typography>
            )}
          </TabPanel>
          <TabPanel index={1} value={tab}>
            {historyMeasurements?.length ? (
              <>
                <Typography align="center" variant="body2" noWrap>
                  {`${t("sea-level")} | ${t("sea-temperature")}`}
                </Typography>
                <Divider />
                <List>
                  {historyMeasurements?.map((m, index) => {
                    return (
                      <ListItem
                        dense
                        alignItems="center"
                        style={{ textAlign: "center" }}
                        key={m.seaLevel.timestamp.valueOf()}
                        divider={index < historyMeasurements.length - 1}
                      >
                        <ListItemText
                          primary={`${m.seaLevel.value} ${m.seaLevel.unit}${
                            m.seaTemperature
                              ? ` | ${m.seaTemperature.value} ${m.seaTemperature.unit}`
                              : ""
                          }`}
                          secondary={dateFormatter({
                            date: m.seaLevel.timestamp,
                          })}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : (
              <Typography align="center" variant="body2">
                {t("no-measurements")}
              </Typography>
            )}
          </TabPanel>
        </>
      }
    />
  );
};
